<template>
  <modal
    elem-id="modal_pdv_registrar_entrega"
    size="compactDesktop"
    :close-label="t('Fechar')"
    :save-label="t('Confirmar Entrega e Baixar OS')"
    :save-disabled-fn="() => !isFormValid"
    :save-fn="handleConfirm"
    :title="t('Registrar Entrega')"
    :show="isVisible"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible">
      <!-- Loading -->
      <div v-if="isLoadingData" class="modal-loading">
        <i class="pi pi-spinner pi-spin"></i>
        <span>{{ t('Carregando...') }}</span>
      </div>

      <div v-else-if="orderData" class="entrega-modal-content">
        <!-- Order summary -->
        <div class="order-summary-section">
          <h4>{{ t('Dados da Ordem') }}</h4>
          <div class="summary-grid">
            <div class="summary-item">
              <span class="summary-item-label">{{ t('OS') }}</span>
              <span class="summary-item-value">#{{ orderData.so_order_id }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-item-label">{{ t('Cliente') }}</span>
              <span class="summary-item-value">{{ orderData.so_order_client_name || t('Sem cliente') }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-item-label">{{ t('Data') }}</span>
              <span class="summary-item-value">{{ formatOrderDate(orderData.so_order_created_at) }}</span>
            </div>
          </div>
        </div>

        <!-- Items list -->
        <div class="items-section" v-if="orderItems.length > 0">
          <h4>{{ t('Itens') }}</h4>
          <div class="items-list">
            <div class="item-row" v-for="item in orderItems" :key="item.so_order_item_id">
              <span class="item-description">{{ item.so_order_item_description }}</span>
              <span class="item-price">{{ n((item.so_order_item_total || 0) / 100, 'currency') }}</span>
            </div>
          </div>
        </div>

        <!-- Financial summary -->
        <div class="financial-section">
          <h4>{{ t('Resumo Financeiro') }}</h4>
          <div class="financial-row">
            <span>{{ t('Valor Total') }}</span>
            <span class="financial-value">{{ n((orderData.so_order_total || 0) / 100, 'currency') }}</span>
          </div>
          <div class="financial-row">
            <span>{{ t('Valor Recebido') }}</span>
            <span class="financial-value">{{ n((orderData.so_order_total_recebido || 0) / 100, 'currency') }}</span>
          </div>
          <div class="financial-row financial-remaining">
            <span>{{ t('Valor Restante') }}</span>
            <span class="financial-value" :class="{ 'value-zero': valorRestante === 0 }">
              {{ n(valorRestante / 100, 'currency') }}
            </span>
          </div>
        </div>

        <!-- Payment form (only if there is remaining balance) -->
        <div class="payment-section" v-if="valorRestante > 0">
          <h4>{{ t('Pagamento') }}</h4>
          <div class="form-field">
            <select-list
              elem-id="entregaFormaPagamento"
              v-model="formaPagamentoId"
              :itens-select="paymentMethodOptions"
              :label="t('Forma de Pagamento')"
              :placeholder="t('Selecione a forma de pagamento')"
              required
              data-testid="pdv-entrega-forma-pagamento"
            />
          </div>
          <div class="form-field">
            <input-currency
              v-model="valorRecebido"
              elem-id="pdv_entrega_valor_recebido"
              :placeholder="t('Valor')"
              data-testid="pdv-entrega-valor-recebido"
            />
          </div>
        </div>

        <!-- WhatsApp receipt toggle -->
        <div class="whatsapp-section">
          <input-toggle
            v-model="sendWhatsappReceipt"
            :label="t('Enviar recibo por WhatsApp')"
            data-testid="pdv-entrega-whatsapp-receipt-toggle"
          />
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useSOEntregas } from '../../composables/useSOEntregas';
import { useFiltro } from '../../composables/useFiltro';
import { log, error as logError } from '../../utils/DebugLogger';
import InputToggle from '../../inputs/InputToggle.vue';

type Id = number | string;

interface OrderData {
  so_order_id?: Id;
  so_order_client_name?: string;
  so_order_created_at?: string;
  so_order_total?: number;
  so_order_total_recebido?: number;
  [key: string]: unknown;
}

interface OrderItem {
  so_order_item_id?: Id;
  so_order_item_description?: string;
  so_order_item_total?: number;
  [key: string]: unknown;
}

interface PaymentMethod {
  forma_pagamento_id?: Id;
  forma_pagamento_nome?: string;
}

interface PaymentMethodOption {
  ID: Id | undefined;
  Name: string | undefined;
}

interface OpenModalPayload {
  orderId?: Id;
}

const { t, n } = useI18n();
const {
  getOrder,
  fetchOrderItems,
  registerPayment,
  fetchPaymentMethods
} = useSOEntregas();
const { selectedFranchises } = useFiltro();

const isVisible = ref<boolean>(false);
const isLoadingData = ref<boolean>(false);
const isSaving = ref<boolean>(false);
const orderData = ref<OrderData | null>(null);
const orderItems = ref<OrderItem[]>([]);
const paymentMethods = ref<PaymentMethod[]>([]);
const formaPagamentoId = ref<Id | null>(null);
const valorRecebido = ref<number>(0);
const sendWhatsappReceipt = ref<boolean>(false);

const valorRestante = computed<number>(() => {
  if (!orderData.value) return 0;
  return Math.max(0, (orderData.value.so_order_total || 0) - (orderData.value.so_order_total_recebido || 0));
});

const paymentMethodOptions = computed<PaymentMethodOption[]>(() =>
  paymentMethods.value.map((pm: PaymentMethod) => ({
    ID: pm.forma_pagamento_id,
    Name: pm.forma_pagamento_nome
  }))
);

const isFormValid = computed<boolean>(() => {
  if (isSaving.value) return false;
  if (valorRestante.value > 0) {
    return !!formaPagamentoId.value && valorRecebido.value > 0;
  }
  return true;
});

const currentFranchiseId = computed<Id | null>(() => {
  const list = selectedFranchises.value as { id?: Id }[] | undefined;
  if (list && list.length > 0) {
    return list[0].id ?? null;
  }
  return null;
});

const formatOrderDate = (dateStr?: string): string => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

const loadOrderData = async (orderId: Id): Promise<void> => {
  log('PDVRegistrarEntregaModal', 'loadOrderData', 'Loading order data', orderId);
  isLoadingData.value = true;
  try {
    const orderIdNum = Number(orderId);
    const [order, items, methods] = await Promise.all([
      getOrder(orderIdNum),
      fetchOrderItems(orderIdNum),
      fetchPaymentMethods()
    ]) as unknown as [OrderData, OrderItem[], PaymentMethod[]];
    orderData.value = order;
    orderItems.value = items;
    paymentMethods.value = methods;
    valorRecebido.value = Math.max(0, (order.so_order_total || 0) - (order.so_order_total_recebido || 0));
    log('PDVRegistrarEntregaModal', 'loadOrderData', 'Data loaded', {
      orderId: order.so_order_id,
      items: items.length,
      methods: methods.length
    });
  } catch (err) {
    logError('PDVRegistrarEntregaModal', 'loadOrderData', 'Error', err);
    eventBus.emit('globalMessage.error', t('Erro ao carregar dados da ordem'));
  } finally {
    isLoadingData.value = false;
  }
};

const handleConfirm = async (): Promise<void> => {
  log('PDVRegistrarEntregaModal', 'handleConfirm', 'Confirming delivery');
  isSaving.value = true;
  try {
    // Step 1: Register payment if there is a remaining balance and amount > 0
    if (valorRestante.value > 0 && valorRecebido.value > 0 && orderData.value) {
      const today = new Date().toISOString().split('T')[0];
      await registerPayment({
        recebimento_origem_id: orderData.value.so_order_id,
        recebimento_franquia_id: currentFranchiseId.value,
        recebimento_forma_pagamento_id: formaPagamentoId.value,
        recebimento_valor: valorRecebido.value,
        recebimento_data: today
      });
      log('PDVRegistrarEntregaModal', 'handleConfirm', 'Payment registered');
    }

    // Step 2: WhatsApp receipt (cosmetic - toast only)
    if (sendWhatsappReceipt.value) {
      eventBus.emit('globalMessage.info', t('Em breve - Envio de recibo por WhatsApp'));
    }

    eventBus.emit('pdv.saved');
    eventBus.emit('globalMessage.success', t('Entrega registrada com sucesso'));
    closeModal();
  } catch (err) {
    logError('PDVRegistrarEntregaModal', 'handleConfirm', 'Error', err);
    const message = err instanceof Error ? err.message : (err as { message?: string })?.message;
    eventBus.emit('globalMessage.error', message || t('Erro ao registrar entrega'));
  } finally {
    isSaving.value = false;
  }
};

const openModal = (data?: OpenModalPayload): void => {
  log('PDVRegistrarEntregaModal', 'openModal', 'Opening modal', data);
  isVisible.value = true;
  formaPagamentoId.value = null;
  valorRecebido.value = 0;
  sendWhatsappReceipt.value = false;
  orderData.value = null;
  orderItems.value = [];

  if (data && data.orderId) {
    loadOrderData(data.orderId);
  }
};

const closeModal = (): void => {
  log('PDVRegistrarEntregaModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
  orderData.value = null;
  orderItems.value = [];
  formaPagamentoId.value = null;
  valorRecebido.value = 0;
  sendWhatsappReceipt.value = false;
};

onMounted(() => {
  eventBus.on('modal.pdv-registrar-entrega.open', openModal);
  eventBus.on('modal.pdv-registrar-entrega.close', closeModal);
});

onBeforeUnmount(() => {
  eventBus.off('modal.pdv-registrar-entrega.open', openModal);
  eventBus.off('modal.pdv-registrar-entrega.close', closeModal);
});
</script>

<style scoped>
.modal-loading {
  text-align: center;
  padding: 40px;
  color: var(--p-text-muted-color);
}

.modal-loading i {
  font-size: 2rem;
  margin-bottom: 8px;
  display: block;
}

.entrega-modal-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.order-summary-section h4,
.items-section h4,
.financial-section h4,
.payment-section h4 {
  margin: 0 0 12px 0;
  font-size: 0.95rem;
  color: var(--p-text-muted-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.summary-item-label {
  font-size: 0.8rem;
  color: var(--p-text-muted-color);
}

.summary-item-value {
  font-weight: 600;
}

.items-list {
  border: 1px solid var(--p-surface-200);
  border-radius: 6px;
  overflow: hidden;
}

.item-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid var(--p-surface-200);
}

.item-row:last-child {
  border-bottom: none;
}

.item-description {
  flex: 1;
}

.item-price {
  font-weight: 600;
  margin-left: 12px;
}

.financial-section {
  background: var(--p-surface-50, #fafafa);
  border-radius: 6px;
  padding: 16px;
}

.financial-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
}

.financial-remaining {
  font-weight: 700;
  border-top: 1px solid var(--p-surface-200);
  padding-top: 8px;
  margin-top: 4px;
}

.financial-value {
  font-weight: 600;
}

.value-zero {
  color: #28a745;
}

.payment-section {
  border: 1px solid var(--p-primary-color);
  border-radius: 6px;
  padding: 16px;
  background: var(--p-surface-0);
}

.form-field {
  margin-bottom: 12px;
}

.form-field:last-child {
  margin-bottom: 0;
}

.whatsapp-section {
  padding: 8px 0;
}

.whatsapp-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 0.9rem;
}

.whatsapp-checkbox {
  width: 16px;
  height: 16px;
}

.whatsapp-label .pi-whatsapp {
  color: #25d366;
}
</style>
